import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControl,
	FormControlLabel,
	InputLabel,
	ListItemText,
	MenuItem,
	OutlinedInput,
	Select,
	IconButton,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { useExportAiUsageMutation } from "AiUsage/Api/Mutations/useExportAiUsageMutation";
import { useAiUsageQuery } from "AiUsage/Api/Queries/useAiUsageQuery";
import { AiModelPricesDialog } from "AiUsage/Components/AiModelPricesDialog";
import { AiUsageTable } from "AiUsage/Components/AiUsageTable";
import { AiUsageStatisticDto } from "Infrastructure/Api/Api";
import { AiModelPrice } from "Infrastructure/Api/ApiCustom";
import { AuthenticatedLayout } from "Shared/Layouts/AuthenticatedLayout";
import { useCallback, useMemo, useState } from "react";

const currentYear = new Date().getFullYear();

type SourceOption = {
	source: number;
	name: string;
};

export const AiUsagePage: React.FunctionComponent = () => {
	const { data, isFetching, refetch } = useAiUsageQuery();
	const exportMutation = useExportAiUsageMutation();
	const [selectedSources, setSelectedSources] = useState<number[]>([]);
	const [excludeSysAdmins, setExcludeSysAdmins] = useState(true);
	const [hideEmptyInstances, setHideEmptyInstances] = useState(true);
	const [selectedYear, setSelectedYear] = useState(currentYear);
	const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
	const [isPricesDialogOpen, setIsPricesDialogOpen] = useState(false);
	const [modelPrices, setModelPrices] = useState<AiModelPrice[]>([]);

	const result = data?.status === 200 ? data.data : null;
	const instances = result?.instances ?? [];

	const sourceOptions = useMemo<SourceOption[]>(() => {
		const sources = new Map<number, string>();
		for (const item of instances.flatMap((x) => x.items)) {
			sources.set(item.source, item.sourceName ?? String(item.source));
		}
		return Array.from(sources, ([source, name]) => ({ source, name })).sort(
			(a, b) => a.source - b.source,
		);
	}, [instances]);

	const isIncluded = useCallback(
		(item: AiUsageStatisticDto) =>
			(!excludeSysAdmins || !item.isSysAdmin) &&
			(selectedSources.length === 0 || selectedSources.includes(item.source)),
		[excludeSysAdmins, selectedSources],
	);

	const years = useMemo(() => {
		const yearSet = new Set<number>([currentYear]);
		for (const item of instances.flatMap((x) => x.items)) {
			yearSet.add(item.year);
		}
		return Array.from(yearSet).sort((a, b) => b - a);
	}, [instances]);

	const olderYear = years.find((year) => year < selectedYear);
	const newerYear = [...years].reverse().find((year) => year > selectedYear);

	const handleSourcesChange =(event: SelectChangeEvent<number[]>) => {
		const value = event.target.value;
		setSelectedSources(typeof value === "string" ? value.split(",").map(Number) : value);
	};

	const failedInstances = instances.filter((x) => !x.isSuccess);

	const deviceOptions = useMemo(
		() => instances.map((x) => x.deviceName).sort((a, b) => a.localeCompare(b)),
		[instances],
	);

	const filteredInstances = useMemo(
		() =>
			selectedDevices.length === 0
				? instances
				: instances.filter((x) => selectedDevices.includes(x.deviceName)),
		[instances, selectedDevices],
	);

	const exportDeviceNames = filteredInstances
		.filter((x) => x.items.some((item) => item.year === selectedYear && isIncluded(item)))
		.map((x) => x.deviceName);

	const exportModels = useMemo(() => {
		const models = new Set<string>();
		for (const instance of filteredInstances) {
			for (const item of instance.items) {
				if (item.year === selectedYear && isIncluded(item)) {
					models.add(item.aiModel ?? "");
				}
			}
		}
		return Array.from(models).sort((a, b) => a.localeCompare(b));
	}, [filteredInstances, selectedYear, isIncluded]);

	const runExport = (includeDetail: boolean, prices: AiModelPrice[]) =>
		exportMutation.mutate(
			{
				year: selectedYear,
				sources: selectedSources,
				excludeSysAdmins,
				deviceNames: exportDeviceNames,
				includeDetail,
				modelPrices: prices,
			},
			{ onSuccess: () => setIsPricesDialogOpen(false) },
		);

	return (
		<AuthenticatedLayout title="AI usage">
			<Stack spacing={2}>
				<Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
					<Paper variant="outlined" sx={{ display: "flex", alignItems: "center", height: 40 }}>
						<IconButton
							size="small"
							disabled={olderYear === undefined}
							onClick={() => olderYear !== undefined && setSelectedYear(olderYear)}
						>
							<ChevronLeftIcon />
						</IconButton>
						<Typography
							variant="subtitle1"
							color="primary"
							sx={{ fontWeight: 600, minWidth: 56, textAlign: "center" }}
						>
							{selectedYear}
						</Typography>
						<IconButton
							size="small"
							disabled={newerYear === undefined}
							onClick={() => newerYear !== undefined && setSelectedYear(newerYear)}
						>
							<ChevronRightIcon />
						</IconButton>
					</Paper>
					<FormControl size="small" sx={{ minWidth: 300 }}>
						<InputLabel id="ai-usage-source-label">Zdroj</InputLabel>
						<Select
							labelId="ai-usage-source-label"
							multiple
							value={selectedSources}
							onChange={handleSourcesChange}
							input={<OutlinedInput label="Zdroj" />}
							renderValue={(selected) =>
								sourceOptions
									.filter((x) => selected.includes(x.source))
									.map((x) => x.name)
									.join(", ")
							}
						>
							{sourceOptions.map((option) => (
								<MenuItem key={option.source} value={option.source}>
									<Checkbox checked={selectedSources.includes(option.source)} />
									<ListItemText primary={option.name} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Autocomplete
						multiple
						size="small"
						limitTags={2}
						disableCloseOnSelect
						options={deviceOptions}
						value={selectedDevices}
						onChange={(_e, value) => setSelectedDevices(value)}
						sx={{ minWidth: 300, maxWidth: 500 }}
						renderOption={(props, option, { selected }) => (
							<li {...props} key={option}>
								<Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
								{option}
							</li>
						)}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Instance"
								placeholder={selectedDevices.length === 0 ? "Všechny instance" : undefined}
							/>
						)}
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={excludeSysAdmins}
								onChange={(e) => setExcludeSysAdmins(e.target.checked)}
							/>
						}
						label="Bez sysadmin účtů"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={hideEmptyInstances}
								onChange={(e) => setHideEmptyInstances(e.target.checked)}
							/>
						}
						label="Skrýt instance bez využití"
					/>
					<Box flexGrow={1} />
					{result && (
						<Typography variant="body2" color="text.secondary">
							Načteno: {new Date(result.dateCollected).toLocaleString("cs-CZ")}
						</Typography>
					)}
					<Button
						variant="outlined"
						startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
						disabled={isFetching}
						onClick={() => refetch()}
					>
						Aktualizovat
					</Button>
					<Button
						variant="contained"
						startIcon={
							exportMutation.isPending ? (
								<CircularProgress size={16} color="inherit" />
							) : (
								<FileDownloadIcon />
							)
						}
						disabled={exportMutation.isPending || exportDeviceNames.length === 0}
						onClick={() => runExport(false, [])}
					>
						Export
					</Button>
					<Button
						variant="contained"
						startIcon={<RequestQuoteIcon />}
						disabled={exportMutation.isPending || exportDeviceNames.length === 0}
						onClick={() => setIsPricesDialogOpen(true)}
					>
						Export s detailem
					</Button>
				</Stack>

				<AiModelPricesDialog
					open={isPricesDialogOpen}
					models={exportModels}
					prices={modelPrices}
					isExporting={exportMutation.isPending}
					onClose={() => setIsPricesDialogOpen(false)}
					onConfirm={(prices) => {
						setModelPrices(prices);
						runExport(true, prices);
					}}
				/>

				{result && result.notRespondingDeviceNames.length > 0 && (
					<Alert severity="warning">
						Neodpověděly instance: {result.notRespondingDeviceNames.join(", ")}
					</Alert>
				)}

				{failedInstances.length > 0 && (
					<Alert severity="error">
						{failedInstances.map((x) => (
							<div key={x.deviceName}>
								{x.deviceName}: {x.errorDescription}
							</div>
						))}
					</Alert>
				)}

				{isFetching && !result ? (
					<Stack alignItems="center" justifyContent="center" minHeight="200px" spacing={2}>
						<CircularProgress />
						<Typography variant="body2" color="text.secondary">
							Načítám data z instancí...
						</Typography>
					</Stack>
				) : (
					<AiUsageTable
						instances={filteredInstances}
						year={selectedYear}
						isIncluded={isIncluded}
						hideEmptyInstances={hideEmptyInstances}
					/>
				)}
			</Stack>
		</AuthenticatedLayout>
	);
};
